import { jsxs as _jsxs, Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
const FG_TONE = {
    default: "",
    muted: "text-ink-400",
    accent: "text-accent-300",
    // Per-theme stops: 300/400 are bright enough on dark, but only ~3:1
    // on a near-white light bg. Switch to 700 in light mode so it stays
    // legible. Same idea applies to amber + red.
    success: "text-emerald-300 [[data-theme=light]_&]:text-emerald-700",
    warning: "text-amber-300 [[data-theme=light]_&]:text-amber-700",
    danger: "text-red-300 [[data-theme=light]_&]:text-red-700",
};
const BG_TONE = {
    none: "",
    header: "bg-ink-700/70",
    subtotal: "bg-accent-500/10",
    total: "bg-accent-500/25",
    input: "bg-emerald-500/10",
    output: "bg-amber-500/15",
    note: "bg-sky-500/10",
};
const ALIGN_CLASS = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
};
function expandStyles(styles) {
    const out = new Map();
    if (!styles)
        return out;
    for (const s of styles) {
        const ref = (s.ref ?? "").toUpperCase();
        if (!ref)
            continue;
        if (ref.includes(":")) {
            const [a, b] = ref.split(":", 2);
            const [c1, r1] = parseRef(a);
            const [c2, r2] = parseRef(b);
            const cmin = Math.min(c1, c2);
            const cmax = Math.max(c1, c2);
            const rmin = Math.min(r1, r2);
            const rmax = Math.max(r1, r2);
            for (let r = rmin; r <= rmax; r++) {
                for (let c = cmin; c <= cmax; c++) {
                    out.set(makeRef(c, r), s);
                }
            }
        }
        else {
            out.set(ref, s);
        }
    }
    return out;
}
function applyNumFormat(value, fmt) {
    if (fmt == null || typeof value !== "number" || !Number.isFinite(value))
        return null;
    // Detect parts. We're doing a simple subset of Excel's format specifier.
    const isPercent = fmt.includes("%");
    const hasComma = fmt.includes(",");
    // Currency prefix/suffix: anything before the first 0/#/, /. that isn't whitespace.
    const m = /^([^0#,.]*)([0#,.]+%?)([^0#,.]*)$/.exec(fmt);
    let prefix = "";
    let suffix = "";
    let body = fmt;
    if (m) {
        prefix = m[1];
        body = m[2];
        suffix = m[3];
    }
    // Decimal places from the body after the dot.
    const dot = body.indexOf(".");
    const decimals = dot >= 0 ? body.slice(dot + 1).replace(/[^0#]/g, "").length : 0;
    let scaled = value;
    if (isPercent)
        scaled = value * 100;
    let s = scaled.toFixed(decimals);
    if (hasComma) {
        const [intPart, fracPart] = s.split(".");
        const sign = intPart.startsWith("-") ? "-" : "";
        const grouped = (sign ? intPart.slice(1) : intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        s = sign + grouped + (fracPart ? "." + fracPart : "");
    }
    if (isPercent)
        s = s + "%";
    return prefix + s + suffix;
}
const COL_WIDTH = 110;
const ROW_HEIGHT = 28;
export function SpreadsheetGrid({ cells, preview, onChange, rows: rowsProp, cols: colsProp, onInsertRow, onDeleteRow, onInsertCol, onDeleteCol, clearRangeRef, }) {
    // Visible row/col counts. Floor stays at 30×12 (rowsProp / colsProp)
    // to keep small models from looking sparse; ceiling at 200×52
    // (through AZ) prevents a runaway sheet. Two layers stack:
    //   1. content auto-grow — derived from `cells` so an inserted ref
    //      past the current visible edge becomes visible automatically.
    //   2. user delta — bumped on Insert row/col, decremented on
    //      Delete. Lets a small model gain visible structure without
    //      requiring the user to type far enough out to trigger the
    //      auto-grow floor.
    const baseRows = rowsProp ?? 30;
    const baseCols = colsProp ?? 12;
    const [rowDelta, setRowDelta] = useState(0);
    const [colDelta, setColDelta] = useState(0);
    const { rows, cols } = useMemo(() => {
        let maxRow = 0;
        let maxCol = 0;
        for (const ref of Object.keys(cells || {})) {
            const m = /^([A-Z]+)(\d+)$/.exec(ref);
            if (!m)
                continue;
            const r = parseInt(m[2], 10);
            const c = colIndex(m[1]);
            if (r > maxRow)
                maxRow = r;
            if (c + 1 > maxCol)
                maxCol = c + 1;
        }
        return {
            rows: Math.min(200, Math.max(baseRows + rowDelta, maxRow + 5)),
            cols: Math.min(52, Math.max(baseCols + colDelta, maxCol + 2)),
        };
    }, [cells, baseRows, baseCols, rowDelta, colDelta]);
    const [selected, setSelected] = useState("A1");
    // When a range is active, `rangeAnchor` holds the cell where the user
    // started the selection; the active cell is `selected`.
    const [rangeAnchor, setRangeAnchor] = useState(null);
    const [editing, setEditing] = useState(null);
    const editingInputRef = useRef(null);
    const gridRef = useRef(null);
    // Caret position inside the editing input. Tracked from the input's
    // onSelect so that mouse-picking another cell knows where to insert
    // the new ref (and which existing token to replace).
    const [caret, setCaret] = useState({ start: 0, end: 0 });
    // Active range pick: mousedown on a cell while editing a formula starts
    // this; mouse-enter on cells extends the second endpoint; mouseup
    // commits as `A1` or `A1:B3` into the formula at the caret.
    const [pickDrag, setPickDrag] = useState(null);
    // True only when the editor is open AND its text is a formula —
    // gates click-to-pick and the colored highlight overlays.
    const inFormulaEdit = editing != null && editing.text.startsWith("=");
    // Right-click context menu on row / column headers.
    const [headerMenu, setHeaderMenu] = useState(null);
    // Close the menu on any outside click / Escape.
    useEffect(() => {
        if (!headerMenu)
            return;
        const close = () => setHeaderMenu(null);
        window.addEventListener("click", close);
        window.addEventListener("contextmenu", close);
        return () => {
            window.removeEventListener("click", close);
            window.removeEventListener("contextmenu", close);
        };
    }, [headerMenu]);
    // Focus the grid on mount and after every commit / cancel so keyboard
    // navigation just works.
    useEffect(() => {
        if (!editing)
            gridRef.current?.focus();
    }, [editing]);
    useEffect(() => {
        if (editing && editingInputRef.current) {
            editingInputRef.current.focus();
            editingInputRef.current.select();
        }
    }, [editing?.ref]);
    const colLabels = useMemo(() => Array.from({ length: cols }, (_, i) => colLabel(i)), [cols]);
    const rowLabels = useMemo(() => Array.from({ length: rows }, (_, i) => i + 1), [rows]);
    const styleMap = useMemo(() => expandStyles(preview?.styles), [preview?.styles]);
    function rangeBox() {
        const [sc, sr] = parseRef(selected);
        if (!rangeAnchor)
            return { c1: sc, r1: sr, c2: sc, r2: sr };
        const [ac, ar] = parseRef(rangeAnchor);
        return {
            c1: Math.min(ac, sc),
            r1: Math.min(ar, sr),
            c2: Math.max(ac, sc),
            r2: Math.max(ar, sr),
        };
    }
    const inRange = useMemo(() => {
        const set = new Set();
        if (!rangeAnchor)
            return set;
        const { c1, r1, c2, r2 } = rangeBox();
        for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++)
                set.add(makeRef(c, r));
        }
        return set;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeAnchor, selected]);
    function rangeCells() {
        const { c1, r1, c2, r2 } = rangeBox();
        const out = [];
        for (let r = r1; r <= r2; r++) {
            for (let c = c1; c <= c2; c++)
                out.push(makeRef(c, r));
        }
        return out;
    }
    // Map cell ref → highlight color for refs/ranges in the formula being
    // edited. Each unique ref gets a color from REF_COLORS; cells covered
    // by overlapping ranges keep the FIRST color assigned.
    const highlightMap = useMemo(() => {
        const map = new Map();
        if (!inFormulaEdit)
            return map;
        const tokens = parseRefsAndRanges(editing.text);
        tokens.forEach((tok, i) => {
            const color = REF_COLORS[i % REF_COLORS.length];
            for (const c of tok.cells)
                if (!map.has(c))
                    map.set(c, color);
        });
        return map;
    }, [inFormulaEdit, editing?.text]);
    // Cells currently inside the active pick rectangle (during mouse-drag).
    const pickCells = useMemo(() => {
        const out = new Set();
        if (!pickDrag)
            return out;
        const [c1, r1] = parseRef(pickDrag.a);
        const [c2, r2] = parseRef(pickDrag.b);
        for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
            for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
                out.add(makeRef(c, r));
            }
        }
        return out;
    }, [pickDrag]);
    function rangeAsTSV() {
        const { c1, r1, c2, r2 } = rangeBox();
        const lines = [];
        for (let r = r1; r <= r2; r++) {
            const row = [];
            for (let c = c1; c <= c2; c++)
                row.push(cells[makeRef(c, r)] ?? "");
            lines.push(row.join("\t"));
        }
        return lines.join("\n");
    }
    function commit() {
        if (!editing)
            return;
        const text = editing.text;
        const next = { ...cells };
        if (text === "")
            delete next[editing.ref];
        else
            next[editing.ref] = text;
        onChange(next);
        setEditing(null);
    }
    function cancel() {
        setEditing(null);
    }
    function startEdit(ref, initial) {
        setSelected(ref);
        setRangeAnchor(null);
        setEditing({ ref, text: initial ?? cells[ref] ?? "" });
    }
    function move(dr, dc, extend = false) {
        const [c, r] = parseRef(selected);
        const nc = clamp(c + dc, 0, cols - 1);
        const nr = clamp(r + dr, 0, rows - 1);
        if (extend) {
            if (!rangeAnchor)
                setRangeAnchor(selected);
        }
        else {
            setRangeAnchor(null);
        }
        setSelected(makeRef(nc, nr));
    }
    function clearRange() {
        const refs = rangeCells();
        if (refs.length === 0)
            return;
        const next = { ...cells };
        for (const r of refs)
            delete next[r];
        onChange(next);
    }
    // Expose clearRange to the parent modal. The modal's document-capture
    // handler swallows Delete before it ever reaches the grid div, so we
    // surface the latest closure here for it to call directly.
    useEffect(() => {
        if (!clearRangeRef)
            return;
        clearRangeRef.current = clearRange;
        return () => {
            if (clearRangeRef.current === clearRange)
                clearRangeRef.current = null;
        };
    });
    function insertRefAtCaret(refToInsert) {
        if (!editing)
            return;
        const text = editing.text;
        const tokenStart = findRefTokenStart(text, caret.start);
        const newText = text.slice(0, tokenStart) + refToInsert + text.slice(caret.end);
        const newCaret = tokenStart + refToInsert.length;
        setEditing({ ref: editing.ref, text: newText });
        setCaret({ start: newCaret, end: newCaret });
        requestAnimationFrame(() => {
            const inp = editingInputRef.current;
            if (inp) {
                inp.focus();
                inp.setSelectionRange(newCaret, newCaret);
            }
        });
    }
    // While a range pick is active, mouseup anywhere commits the range.
    // The listener captures the latest editing/caret via closure since the
    // effect re-subscribes whenever those change.
    useEffect(() => {
        if (!pickDrag)
            return;
        function up() {
            const cur = pickDrag;
            if (!cur)
                return;
            const text = cur.a === cur.b ? cur.a : rangeOf(cur.a, cur.b);
            insertRefAtCaret(text);
            setPickDrag(null);
        }
        window.addEventListener("mouseup", up);
        return () => window.removeEventListener("mouseup", up);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickDrag, editing, caret]);
    async function copyToClipboard(cut) {
        const tsv = rangeAsTSV();
        try {
            await navigator.clipboard.writeText(tsv);
        }
        catch {
            // Fallback: select a hidden textarea and execCommand. Skipped for
            // simplicity — modern browsers support clipboard API on https.
        }
        if (cut)
            clearRange();
    }
    async function pasteFromClipboard() {
        let text = "";
        try {
            text = await navigator.clipboard.readText();
        }
        catch {
            return;
        }
        if (!text)
            return;
        const lines = text.replace(/\r\n/g, "\n").split("\n");
        while (lines.length > 0 && lines[lines.length - 1] === "")
            lines.pop();
        if (lines.length === 0)
            return;
        const [startC, startR] = parseRef(selected);
        const next = { ...cells };
        let lastC = startC;
        let lastR = startR;
        for (let i = 0; i < lines.length; i++) {
            const cellsInRow = lines[i].split("\t");
            for (let j = 0; j < cellsInRow.length; j++) {
                const r = startR + i;
                const c = startC + j;
                if (c >= cols || r >= rows)
                    continue;
                const ref = makeRef(c, r);
                const v = cellsInRow[j];
                if (v === "")
                    delete next[ref];
                else
                    next[ref] = v;
                lastC = Math.max(lastC, c);
                lastR = Math.max(lastR, r);
            }
        }
        onChange(next);
        if (lastC === startC && lastR === startR) {
            setRangeAnchor(null);
        }
        else {
            setRangeAnchor(makeRef(startC, startR));
        }
        setSelected(makeRef(lastC, lastR));
    }
    function onKey(e) {
        if (editing)
            return;
        const meta = e.ctrlKey || e.metaKey;
        // Clipboard ops
        if (meta && (e.key === "c" || e.key === "C")) {
            e.preventDefault();
            e.stopPropagation();
            copyToClipboard(false);
            return;
        }
        if (meta && (e.key === "x" || e.key === "X")) {
            e.preventDefault();
            e.stopPropagation();
            copyToClipboard(true);
            return;
        }
        if (meta && (e.key === "v" || e.key === "V")) {
            e.preventDefault();
            e.stopPropagation();
            pasteFromClipboard();
            return;
        }
        if (meta && (e.key === "a" || e.key === "A")) {
            e.preventDefault();
            e.stopPropagation();
            setRangeAnchor("A1");
            setSelected(makeRef(cols - 1, rows - 1));
            return;
        }
        // Navigation
        const NAV_KEYS = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Tab", "Enter", "F2", "Escape", "Delete", "Backspace", "Home", "End", "PageUp", "PageDown"];
        if (NAV_KEYS.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (e.key === "ArrowDown")
            move(1, 0, e.shiftKey);
        else if (e.key === "ArrowUp")
            move(-1, 0, e.shiftKey);
        else if (e.key === "ArrowLeft")
            move(0, -1, e.shiftKey);
        else if (e.key === "ArrowRight")
            move(0, 1, e.shiftKey);
        else if (e.key === "Tab")
            move(0, e.shiftKey ? -1 : 1);
        else if (e.key === "Enter" || e.key === "F2")
            startEdit(selected);
        else if (e.key === "Delete" || e.key === "Backspace")
            clearRange();
        else if (e.key === "Escape")
            setRangeAnchor(null);
        else if (e.key === "Home") {
            const [, r] = parseRef(selected);
            if (e.shiftKey && !rangeAnchor)
                setRangeAnchor(selected);
            if (!e.shiftKey)
                setRangeAnchor(null);
            setSelected(makeRef(0, r));
        }
        else if (e.key === "End") {
            const [, r] = parseRef(selected);
            if (e.shiftKey && !rangeAnchor)
                setRangeAnchor(selected);
            if (!e.shiftKey)
                setRangeAnchor(null);
            setSelected(makeRef(cols - 1, r));
        }
        else if (e.key === "PageDown") {
            move(10, 0, e.shiftKey);
        }
        else if (e.key === "PageUp") {
            move(-10, 0, e.shiftKey);
        }
        else if (e.key.length === 1 && !meta && !e.altKey) {
            // Typed printable character — start editing with it.
            e.stopPropagation();
            startEdit(selected, e.key);
        }
    }
    function onCellClick(ref, e) {
        // In formula-edit mode the click is consumed by the picker
        // (see onCellMouseDown); don't move the selection or restart edit.
        if (inFormulaEdit)
            return;
        if (e.detail === 2 && !e.shiftKey) {
            startEdit(ref);
            return;
        }
        if (e.shiftKey) {
            if (!rangeAnchor)
                setRangeAnchor(selected);
            setSelected(ref);
        }
        else {
            setRangeAnchor(null);
            setSelected(ref);
        }
        gridRef.current?.focus();
    }
    function onCellMouseDown(ref, e) {
        // Clicks INSIDE the editing input itself (caret moves, drag-select
        // text) must reach the input — don't start a pick.
        if (e.target instanceof HTMLInputElement)
            return;
        if (!inFormulaEdit)
            return;
        if (e.button !== 0)
            return;
        // preventDefault keeps focus on the editing input (no blur → no
        // commit), and stops the browser's text-selection drag.
        e.preventDefault();
        setPickDrag({ a: ref, b: ref });
    }
    function onCellMouseEnter(ref) {
        if (!pickDrag)
            return;
        setPickDrag((prev) => (prev && prev.b !== ref ? { ...prev, b: ref } : prev));
    }
    return (_jsxs("div", { className: "flex h-full min-h-0 flex-col", children: [headerMenu && (_jsx("div", { className: "fixed z-50 min-w-[180px] rounded-md border border-ink-700 bg-ink-800 py-1 text-sm text-ink-100 shadow-lg", style: { left: headerMenu.x, top: headerMenu.y }, onClick: (e) => e.stopPropagation(), onContextMenu: (e) => e.preventDefault(), children: headerMenu.kind === "row" ? (_jsxs(_Fragment, { children: [onInsertRow && (_jsxs(MenuButton, { onClick: () => {
                                onInsertRow(headerMenu.row);
                                setRowDelta((d) => d + 1);
                                setHeaderMenu(null);
                            }, children: ["Insert row above (row ", headerMenu.row, ")"] })), onInsertRow && (_jsxs(MenuButton, { onClick: () => {
                                onInsertRow(headerMenu.row + 1);
                                setRowDelta((d) => d + 1);
                                setHeaderMenu(null);
                            }, children: ["Insert row below (row ", headerMenu.row, ")"] })), onDeleteRow && (_jsxs(MenuButton, { danger: true, onClick: () => {
                                onDeleteRow(headerMenu.row);
                                setRowDelta((d) => Math.max(-baseRows + 1, d - 1));
                                setHeaderMenu(null);
                            }, children: ["Delete row ", headerMenu.row] }))] })) : (_jsxs(_Fragment, { children: [onInsertCol && (_jsxs(MenuButton, { onClick: () => {
                                onInsertCol(headerMenu.colIdx);
                                setColDelta((d) => d + 1);
                                setHeaderMenu(null);
                            }, children: ["Insert column left (", colLabel(headerMenu.colIdx), ")"] })), onInsertCol && (_jsxs(MenuButton, { onClick: () => {
                                onInsertCol(headerMenu.colIdx + 1);
                                setColDelta((d) => d + 1);
                                setHeaderMenu(null);
                            }, children: ["Insert column right (", colLabel(headerMenu.colIdx), ")"] })), onDeleteCol && (_jsxs(MenuButton, { danger: true, onClick: () => {
                                onDeleteCol(headerMenu.colIdx);
                                setColDelta((d) => Math.max(-baseCols + 1, d - 1));
                                setHeaderMenu(null);
                            }, children: ["Delete column ", colLabel(headerMenu.colIdx)] }))] })) })), _jsx(FormulaBar, { cellRef: selected, formula: cells[selected] ?? "", editing: editing, onStartEdit: () => startEdit(selected), onCommit: commit, onCancel: cancel, onChange: (t) => setEditing(editing ? { ...editing, text: t } : { ref: selected, text: t }) }), _jsx("div", { ref: gridRef, className: "relative min-h-0 flex-1 overflow-auto bg-ink-900 outline-none", tabIndex: 0, onKeyDown: onKey, children: _jsxs("table", { className: "border-separate", style: { borderSpacing: 0, fontVariantNumeric: "tabular-nums" }, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "sticky top-0 left-0 z-30 border-b border-r border-ink-700 bg-ink-800 text-xs text-ink-500", style: { width: 40, height: ROW_HEIGHT } }), colLabels.map((c, idx) => (_jsx("th", { className: "sticky top-0 z-20 border-b border-r border-ink-700 bg-ink-800 text-xs text-ink-300 font-mono", style: { width: COL_WIDTH, height: ROW_HEIGHT, cursor: (onInsertCol || onDeleteCol) ? "context-menu" : undefined }, onContextMenu: (e) => {
                                            if (!onInsertCol && !onDeleteCol)
                                                return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setHeaderMenu({ kind: "col", colIdx: idx, x: e.clientX, y: e.clientY });
                                        }, children: c }, c)))] }) }), _jsx("tbody", { children: rowLabels.map((r) => (_jsxs("tr", { children: [_jsx("th", { className: "sticky left-0 z-10 border-b border-r border-ink-700 bg-ink-800 text-center text-xs text-ink-300 font-mono", style: { width: 40, height: ROW_HEIGHT, cursor: (onInsertRow || onDeleteRow) ? "context-menu" : undefined }, onContextMenu: (e) => {
                                            if (!onInsertRow && !onDeleteRow)
                                                return;
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setHeaderMenu({ kind: "row", row: r, x: e.clientX, y: e.clientY });
                                        }, children: r }), colLabels.map((c) => {
                                        const ref = `${c}${r}`;
                                        const formula = cells[ref] ?? "";
                                        const result = preview?.cells[ref];
                                        const isSelected = selected === ref;
                                        const isInRange = inRange.has(ref);
                                        const isEditing = editing?.ref === ref;
                                        const style = styleMap.get(ref);
                                        const formatted = style?.num_format
                                            ? applyNumFormat(result?.value, style.num_format)
                                            : null;
                                        const display = formula === ""
                                            ? ""
                                            : result?.error
                                                ? result.error
                                                : formatted !== null
                                                    ? formatted
                                                    : result?.value !== undefined
                                                        ? formatValue(result.value)
                                                        : formula;
                                        const baseTone = result?.error
                                            ? "text-red-300"
                                            : isFormula(formula)
                                                ? "text-ink-100"
                                                : "text-ink-200";
                                        const fgClass = style?.fg ? FG_TONE[style.fg] : "";
                                        const bgClass = style?.bg ? BG_TONE[style.bg] : "";
                                        const alignClass = style?.align
                                            ? ALIGN_CLASS[style.align]
                                            : typeof result?.value === "number"
                                                ? "text-right"
                                                : "text-left";
                                        const highlightColor = highlightMap.get(ref);
                                        const isPicking = pickCells.has(ref);
                                        // box-shadow stacks with the existing outline used for the
                                        // hard "selected" cell highlight: pick rectangle (dashed
                                        // accent), highlighted refs (color from REF_COLORS), and
                                        // self-ref (the cell being edited) all share the same
                                        // technique and don't fight the outline.
                                        const overlayShadow = isPicking
                                            ? `inset 0 0 0 2px ${REF_PICK_COLOR}`
                                            : highlightColor
                                                ? `inset 0 0 0 2px ${highlightColor}`
                                                : undefined;
                                        return (_jsx("td", { onClick: (e) => onCellClick(ref, e), onMouseDown: (e) => onCellMouseDown(ref, e), onMouseEnter: () => onCellMouseEnter(ref), className: [
                                                "relative cursor-cell border-b border-r border-ink-800",
                                                bgClass,
                                                isInRange && !isSelected && !bgClass ? "bg-accent-500/15" : "",
                                                isSelected
                                                    ? "outline outline-2 outline-accent-500/80 outline-offset-[-2px]"
                                                    : "",
                                            ].join(" "), style: { width: COL_WIDTH, height: ROW_HEIGHT, boxShadow: overlayShadow }, children: isEditing ? (_jsx("input", { ref: editingInputRef, className: "absolute inset-0 w-full bg-ink-700 px-1 font-mono text-xs text-ink-50 outline-none", value: editing.text, onChange: (e) => {
                                                    setEditing({ ref, text: e.target.value });
                                                    const inp = e.currentTarget;
                                                    setCaret({
                                                        start: inp.selectionStart ?? 0,
                                                        end: inp.selectionEnd ?? 0,
                                                    });
                                                }, onSelect: (e) => {
                                                    const inp = e.currentTarget;
                                                    setCaret({
                                                        start: inp.selectionStart ?? 0,
                                                        end: inp.selectionEnd ?? 0,
                                                    });
                                                }, onBlur: commit, onKeyDown: (e) => {
                                                    // Block these from reaching the editor's React Flow.
                                                    e.stopPropagation();
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        commit();
                                                        move(1, 0);
                                                    }
                                                    else if (e.key === "Tab") {
                                                        e.preventDefault();
                                                        commit();
                                                        move(0, e.shiftKey ? -1 : 1);
                                                    }
                                                    else if (e.key === "Escape") {
                                                        e.preventDefault();
                                                        cancel();
                                                    }
                                                    else if (e.key === "F4") {
                                                        // Excel-style anchor cycle on the ref
                                                        // immediately to the LEFT of the
                                                        // caret (or under the selection).
                                                        e.preventDefault();
                                                        const input = e.currentTarget;
                                                        const cycled = cycleRefAtCaret(editing.text, input.selectionStart ?? editing.text.length, input.selectionEnd ?? editing.text.length);
                                                        if (cycled) {
                                                            setEditing({ ref, text: cycled.text });
                                                            // Restore caret + selection after React updates the input.
                                                            requestAnimationFrame(() => {
                                                                input.setSelectionRange(cycled.start, cycled.end);
                                                            });
                                                        }
                                                    }
                                                } })) : (_jsx("div", { className: [
                                                    "truncate px-1.5 py-1 text-xs",
                                                    fgClass || baseTone,
                                                    alignClass,
                                                    style?.bold ? "font-semibold" : "",
                                                    style?.italic ? "italic" : "",
                                                ].join(" "), title: isComplex(result?.value)
                                                    ? truncated(JSON.stringify(result?.value, null, 2), 800)
                                                    : formula || "", children: display })) }, ref));
                                    })] }, r))) })] }) }), _jsx(CellInspector, { preview: preview, cells: cells, selected: selected }), _jsx(PortStrip, { preview: preview })] }));
}
function CellInspector({ preview, cells, selected, }) {
    const result = preview?.cells[selected];
    if (!result || !isComplex(result.value))
        return null;
    const formula = cells[selected] ?? "";
    const json = JSON.stringify(result.value, null, 2);
    return (_jsxs("div", { className: "border-t border-ink-700 bg-ink-800 px-3 py-2 text-xs", children: [_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("span", { className: "font-mono text-ink-300", children: selected }), _jsx("span", { className: "ml-2 text-ink-500", children: "contains a complex value" })] }), _jsx("button", { className: "text-[11px] text-accent-400 hover:text-accent-300", onClick: () => navigator.clipboard?.writeText(json), children: "copy as JSON" })] }), _jsxs("details", { className: "mt-1", children: [_jsxs("summary", { className: "cursor-pointer text-ink-300 hover:text-ink-100", children: [summarizeComplex(result.value), " \u2014 show full"] }), _jsx("pre", { className: "mt-1 max-h-40 overflow-auto rounded-md border border-ink-700 bg-ink-900 p-2 text-[11px] text-ink-200", children: truncated(json, 5000) })] }), formula && (_jsxs("div", { className: "mt-1 text-[11px] text-ink-500", children: ["formula: ", _jsx("code", { className: "font-mono", children: formula })] }))] }));
}
function summarizeComplex(v) {
    if (Array.isArray(v)) {
        if (v.length === 0)
            return "empty list";
        if (Array.isArray(v[0])) {
            return `2D list ${v.length}×${v[0].length}`;
        }
        return `list of ${v.length}`;
    }
    if (typeof v === "object" && v !== null) {
        const keys = Object.keys(v);
        return `object with ${keys.length} key${keys.length === 1 ? "" : "s"}: ${keys
            .slice(0, 6)
            .join(", ")}${keys.length > 6 ? ", …" : ""}`;
    }
    return "(scalar)";
}
function truncated(s, max) {
    return s.length > max ? s.slice(0, max - 3) + "..." : s;
}
function FormulaBar({ cellRef, formula, editing, onStartEdit, onChange, onCommit, onCancel, }) {
    return (_jsxs("div", { className: "flex items-center gap-2 border-b border-ink-700 bg-ink-800 px-3 py-2 text-sm", children: [_jsx("div", { className: "w-16 text-center font-mono text-xs text-ink-300", children: cellRef }), _jsx("div", { className: "text-ink-600", children: "fx" }), _jsx("input", { className: "flex-1 rounded border border-ink-700 bg-ink-900 px-2 py-1 font-mono text-xs text-ink-100 focus:border-accent-500 focus:outline-none", value: editing ? editing.text : formula, placeholder: 'Type a formula starting with "=" or a literal value', onFocus: () => onStartEdit(), onChange: (e) => onChange(e.target.value), onKeyDown: (e) => {
                    e.stopPropagation();
                    if (e.key === "Enter")
                        onCommit();
                    if (e.key === "Escape")
                        onCancel();
                    if (e.key === "F4" && editing) {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const cycled = cycleRefAtCaret(editing.text, input.selectionStart ?? editing.text.length, input.selectionEnd ?? editing.text.length);
                        if (cycled) {
                            onChange(cycled.text);
                            requestAnimationFrame(() => {
                                input.setSelectionRange(cycled.start, cycled.end);
                            });
                        }
                    }
                } })] }));
}
function PortStrip({ preview }) {
    if (!preview)
        return null;
    const errs = Object.entries(preview.errors);
    return (_jsx("div", { className: "border-t border-ink-700 bg-ink-800 px-3 py-2 text-xs", children: _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx("span", { className: "text-ink-500", children: "Inputs:" }), preview.input_ports.length === 0 ? (_jsx("span", { className: "text-ink-600", children: "none" })) : (preview.input_ports.map((p) => (_jsx("span", { className: "rounded bg-emerald-500/10 px-2 py-0.5 font-mono text-emerald-300 [[data-theme=light]_&]:text-emerald-700", children: p }, p)))), _jsx("span", { className: "ml-4 text-ink-500", children: "Outputs:" }), preview.output_ports.length === 0 ? (_jsx("span", { className: "text-ink-600", children: "none" })) : (preview.output_ports.map((p) => (_jsxs("span", { className: "rounded bg-amber-500/10 px-2 py-0.5 font-mono text-amber-300", children: [p, preview.outputs[p] !== undefined && (_jsxs("span", { className: "ml-1 text-amber-400/80", children: ["= ", formatValue(preview.outputs[p])] }))] }, p)))), errs.length > 0 && (_jsxs(_Fragment, { children: [_jsx("span", { className: "ml-4 text-red-300", children: "Errors:" }), errs.map(([ref, code]) => (_jsxs("span", { className: "rounded bg-red-500/10 px-2 py-0.5 font-mono text-red-300", children: [ref, " ", code] }, ref)))] }))] }) }));
}
// --- helpers ------------------------------------------------------------------
function MenuButton({ children, onClick, danger, }) {
    return (_jsx("button", { type: "button", onClick: onClick, className: "block w-full px-3 py-1.5 text-left text-xs hover:bg-ink-700 " +
            (danger ? "text-red-300" : "text-ink-100"), children: children }));
}
function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
}
// Palette for the per-ref highlight outlines drawn while editing a
// formula. Cycled in order: each unique ref/range token in the formula
// text picks the next color, mirroring Excel's coloured ref marks.
const REF_COLORS = [
    "#60a5fa", // blue-400
    "#a78bfa", // violet-400
    "#34d399", // emerald-400
    "#fb923c", // orange-400
    "#f472b6", // pink-400
    "#22d3ee", // cyan-400
];
// Distinct color for the live pick rectangle while the user is dragging.
const REF_PICK_COLOR = "#facc15"; // yellow-400
function rangeOf(a, b) {
    const m1 = /^([A-Z]+)(\d+)$/.exec(a);
    const m2 = /^([A-Z]+)(\d+)$/.exec(b);
    if (!m1 || !m2)
        return a;
    let c1 = 0;
    for (const ch of m1[1])
        c1 = c1 * 26 + (ch.charCodeAt(0) - 64);
    c1 -= 1;
    let c2 = 0;
    for (const ch of m2[1])
        c2 = c2 * 26 + (ch.charCodeAt(0) - 64);
    c2 -= 1;
    const r1 = parseInt(m1[2], 10) - 1;
    const r2 = parseInt(m2[2], 10) - 1;
    const tl = `${colLabel(Math.min(c1, c2))}${Math.min(r1, r2) + 1}`;
    const br = `${colLabel(Math.max(c1, c2))}${Math.max(r1, r2) + 1}`;
    return `${tl}:${br}`;
}
// Walk back from `pos` over a candidate ref token so the picker can
// REPLACE a partial/complete ref instead of appending one. Returns the
// start index of the token, or `pos` if there's no ref-like token to
// replace.
function findRefTokenStart(text, pos) {
    let i = pos;
    while (i > 0 && /\d/.test(text[i - 1]))
        i--;
    if (i > 0 && text[i - 1] === "$")
        i--;
    while (i > 0 && /[A-Za-z]/.test(text[i - 1]))
        i--;
    if (i > 0 && text[i - 1] === "$")
        i--;
    const sub = text.slice(i, pos);
    // Must contain at least one letter to be a ref token candidate. Pure
    // digits would replace literal numbers like "=2+3" → don't.
    if (sub.length > 0 && /[A-Za-z]/.test(sub) && /^\$?[A-Za-z]{1,3}\$?\d*$/.test(sub)) {
        return i;
    }
    return pos;
}
// Find every cell ref and contiguous range in the formula text. Returns
// in source order so the highlight palette assigns colors consistent
// with reading order.
function parseRefsAndRanges(text) {
    const out = [];
    const re = /(\$?[A-Z]{1,3}\$?\d{1,7})(?:\s*:\s*(\$?[A-Z]{1,3}\$?\d{1,7}))?/gi;
    let m;
    while ((m = re.exec(text)) !== null) {
        const a = m[1].replace(/\$/g, "").toUpperCase();
        const b = m[2] ? m[2].replace(/\$/g, "").toUpperCase() : null;
        const cells = new Set();
        if (b) {
            const [c1, r1] = parseRefSimple(a);
            const [c2, r2] = parseRefSimple(b);
            for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
                for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
                    cells.add(`${colLabel(c)}${r + 1}`);
                }
            }
        }
        else {
            cells.add(a);
        }
        out.push({ token: b ? `${a}:${b}` : a, cells });
    }
    return out;
}
function parseRefSimple(ref) {
    const m = /^([A-Z]+)(\d+)$/.exec(ref);
    if (!m)
        return [0, 0];
    let col = 0;
    for (const ch of m[1])
        col = col * 26 + (ch.charCodeAt(0) - 64);
    return [col - 1, parseInt(m[2], 10) - 1];
}
/**
 * Find the cell reference under the caret (or in the active selection)
 * inside a formula text and cycle its anchor state. Returns the new
 * text plus the [start, end] caret range covering the rewritten ref so
 * the caller can reselect it. Returns null if no ref is at/around the
 * caret.
 *
 * Matches Excel's F4 semantics: if the caret is inside or right at the
 * end of a ref, that ref is cycled. If a selection covers exactly one
 * ref, that ref is cycled.
 */
function cycleRefAtCaret(text, caretStart, caretEnd) {
    // Find every cell ref and its position.
    const re = /(\$?)([A-Z]{1,3})(\$?)(\d{1,7})/gi;
    let m;
    let target = null;
    while ((m = re.exec(text)) !== null) {
        const start = m.index;
        const end = start + m[0].length;
        // Caret directly inside the ref OR selection equals exactly the ref.
        const inside = caretStart >= start && caretStart <= end;
        const selectsRef = caretStart === start && caretEnd === end;
        if (inside || selectsRef) {
            target = { start, end, ref: m[0] };
            break;
        }
    }
    if (!target)
        return null;
    // Reuse cycleAnchor from lib/spreadsheet.
    const cycled = cycleAnchorRef(target.ref);
    const newText = text.slice(0, target.start) + cycled + text.slice(target.end);
    return {
        text: newText,
        start: target.start,
        end: target.start + cycled.length,
    };
}
function cycleAnchorRef(ref) {
    const m = /^(\$?)([A-Z]{1,3})(\$?)(\d{1,7})$/i.exec(ref.trim());
    if (!m)
        return ref;
    const colAbs = !!m[1];
    const col = m[2].toUpperCase();
    const rowAbs = !!m[3];
    const row = parseInt(m[4], 10);
    const state = !colAbs && !rowAbs ? 0
        : colAbs && rowAbs ? 1
            : !colAbs && rowAbs ? 2
                : 3;
    const next = (state + 1) % 4;
    const nColAbs = next === 1 || next === 3;
    const nRowAbs = next === 1 || next === 2;
    return (nColAbs ? "$" : "") + col + (nRowAbs ? "$" : "") + row;
}
function colLabel(idx) {
    let s = "";
    let n = idx + 1;
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}
function colIndex(label) {
    let n = 0;
    for (const ch of label)
        n = n * 26 + (ch.charCodeAt(0) - 64);
    return n - 1;
}
function parseRef(ref) {
    const m = /^([A-Z]+)(\d+)$/.exec(ref);
    if (!m)
        return [0, 0];
    let col = 0;
    for (const ch of m[1])
        col = col * 26 + (ch.charCodeAt(0) - 64);
    col -= 1;
    return [col, parseInt(m[2], 10) - 1];
}
function makeRef(col, row) {
    return `${colLabel(col)}${row + 1}`;
}
function isFormula(s) {
    return s.trim().startsWith("=");
}
function formatValue(v) {
    if (v === null || v === undefined)
        return "";
    if (typeof v === "number") {
        if (Number.isInteger(v))
            return v.toString();
        return v.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
    }
    if (typeof v === "boolean")
        return v ? "TRUE" : "FALSE";
    if (Array.isArray(v)) {
        if (v.length === 0)
            return "▦ []";
        if (Array.isArray(v[0])) {
            const rows = v.length;
            const cols = v[0].length;
            return `▦ [${rows}×${cols}]`;
        }
        return `▦ [${v.length}]`;
    }
    if (typeof v === "object") {
        const keys = Object.keys(v);
        return `▦ {${keys.length}}`;
    }
    return String(v);
}
function isComplex(v) {
    return (Array.isArray(v) ||
        (typeof v === "object" && v !== null));
}
